import { memo, useEffect } from 'react'

import { DeleteFive } from '@icon-park/react'

import { BrandResponse } from '@shared/models/brand'

import { Tokens } from '@/assets/tokens'

import { Alert, Api, Confirmation } from '@/modules'

import { Button, Divider, Flex, TableImage } from '@/components'
import { ColumnType, Table } from '@/components/derived'

import { useRouting } from '@/hooks'
import { getFromToElements } from '@/utils/pagination'
import { useCompanyContext } from '../CompanyDetails.context'

export const BrandsTable = memo(Component)

function Component() {
  const { changeQueryParam, currentPage } = useRouting()
  const { isLoadingBrands, brands, reloadBrands, reloadAllBrands } = useCompanyContext()

  const { sendAlert } = Alert.Hook()

  const { open: openDeleteConfirmation } = Confirmation.Hook({
    icon: DeleteFive,
    text: 'Esta ação não tem como ser desfeita. Temas, porta-vozes, regras de prompt e vínculos com postagens dessa marca também serão apagados permanentemente.',
    confirmText: 'Sim! Apagar marca',
  })

  const { isRunning: isDeleting, run: deleteBrand } = Api.Command<void>(Api.Methods.DELETE, 'brands/:id')

  useEffect(() => {
    reloadBrands(currentPage)
  }, [currentPage])

  function onDeleteConfirmationAccepted(id: string): void {
    deleteBrand<void>({
      binds: [['id', id]],
      onSuccess: () => {
        sendAlert({
          type: 'success',
          title: 'Exclusão da marca',
          message: 'Marca excluída com sucesso',
        })
        reloadBrands(currentPage)
        reloadAllBrands()
      },
    })
  }

  function handleEditBrand(brandId: string) {
    changeQueryParam({
      param: 'editBrandId',
      value: brandId,
    })
  }

  const columns: ColumnType<BrandResponse>[] = [
    {
      key: 'logo',
      width: 100,
      render: (_, brand) => <TableImage id={brand.systemLogo} />,
    },
    {
      key: 'name',
      title: 'Nome',
    },
    {
      key: 'options',
      width: 137,
      title: 'Opções',
      align: 'center',
      render: (_, brand) => {
        const isMainBrand = Boolean(brand.mainBrand)

        return (
          <Flex $type="row" $justify="flex-start" $align="stretch">
            <Button.Root
              $variant="link"
              style={{ color: Tokens.ColorUIPrimary, marginRight: '-8px' }}
              onClick={() => handleEditBrand(brand.id)}
              $loading={isDeleting}>
              Editar
            </Button.Root>
            <span
              onClick={() => {
                if (isMainBrand) {
                  sendAlert({
                    type: 'error',
                    title: 'Exclusão não permitida',
                    message: 'A marca principal não pode ser excluída.',
                  })
                }
              }}>
              <Button.Root
                $variant="link"
                style={{
                  color: Tokens.ColorUIDanger,
                  marginLeft: '-8px',
                }}
                onClick={() => {
                  if (isMainBrand) {
                    return
                  }
                  openDeleteConfirmation(() => onDeleteConfirmationAccepted(brand.id))
                }}
                disabled={isDeleting || isMainBrand}
                $loading={isDeleting}>
                Excluir
              </Button.Root>
            </span>
          </Flex>
        )
      },
    },
  ]

  return (
    <>
      <Divider.Root>
        <Divider.Title value="Marcas registradas" />
        <Divider.Subtitle
          value={`${getFromToElements(currentPage, brands?.elements.length ?? 0)}/${brands?.total ?? 0} Entrada(s)`}
        />
      </Divider.Root>
      <Table isLoading={isLoadingBrands} columns={columns} data={brands?.elements} totalPages={brands?.pages} />
    </>
  )
}
